<template>
  <div class="required-docs-table">
    <Spinner
      v-if="loadingDocs"
      class="required-docs-table__spinner"
    />
    <table
      v-else
      class="required-docs-table__table"
    >
      <thead>
        <tr>
          <th
            class="required-docs-table__header-cell required-docs-table__header-cell--date"
            @click="orderByColumn('date', 0)"
          >
            DATA
            <svg-loader
              src="/static/images/icon-sort-arrow.svg"
              class="arrow-direction"
              :class="{
                'arrow-direction__down': actualColumnSortType === 0 && sortDirection === 'asc',
                'arrow-direction__up': actualColumnSortType === 0 && sortDirection === 'desc'
              }"
            />
          </th>
          <th
            class="required-docs-table__header-cell required-docs-table__header-cell--os-lis"
            @click="orderByColumn('osLis', 1)"
          >
            OS LIS
            <md-icon class="icon-status-info">
              info_outline
              <md-tooltip
                md-delay="400"
                md-direction="top"
              >
                Número da Ordem de Serviço do sistema do cliente
              </md-tooltip>
            </md-icon>
            <svg-loader
              src="/static/images/icon-sort-arrow.svg"
              class="arrow-direction"
              :class="{
                'arrow-direction__down': actualColumnSortType === 1 && sortDirection === 'asc',
                'arrow-direction__up': actualColumnSortType === 1 && sortDirection === 'desc'
              }"
            />
          </th>
          <th
            class="required-docs-table__header-cell required-docs-table__header-cell--name"
            @click="orderByColumn('patientName', 2)"
          >
            PACIENTE
            <svg-loader
              src="/static/images/icon-sort-arrow.svg"
              class="arrow-direction"
              :class="{
                'arrow-direction__down': actualColumnSortType === 2 && sortDirection === 'asc',
                'arrow-direction__up': actualColumnSortType === 2 && sortDirection === 'desc'
              }"
            />
          </th>
          <th
            class="required-docs-table__header-cell required-docs-table__header-cell--os-alvaro"
            @click="orderByColumn('numberAlvaroOrder', 3)"
          >
            OS ALVARO
            <svg-loader
              src="/static/images/icon-sort-arrow.svg"
              class="arrow-direction"
              :class="{
                'arrow-direction__down': actualColumnSortType === 3 && sortDirection === 'asc',
                'arrow-direction__up': actualColumnSortType === 3 && sortDirection === 'desc'
              }"
            />
          </th>
          <th
            class="required-docs-table__header-cell required-docs-table__header-cell--status"
            @click="orderByColumn('hasDocPendente', 4)"
          >
            STATUS
            <svg-loader
              src="/static/images/icon-sort-arrow.svg"
              class="arrow-direction"
              :class="{
                'arrow-direction__down': actualColumnSortType === 4 && sortDirection === 'asc',
                'arrow-direction__up': actualColumnSortType === 4 && sortDirection === 'desc'
              }"
            />
          </th>
          <th
            class="required-docs-table__header-cell required-docs-table__header-cell--origem"
            @click="orderByColumn('origin', 5)"
          >
            ORIGEM
            <svg-loader
              src="/static/images/icon-sort-arrow.svg"
              class="arrow-direction"
              :class="{
                'arrow-direction__down': actualColumnSortType === 5 && sortDirection === 'asc',
                'arrow-direction__up': actualColumnSortType === 5 && sortDirection === 'desc'
              }"
            />
          </th>
        </tr>
      </thead>

      <template v-if="docs.length > 0">
        <tbody
          v-for="(item, index) in docs"
          :key="index"
          class="table-body"
          :class="'table-body--' + index"
          @click="toggleCollapse(index)"
        >
          <tr>
            <td class="required-docs-table__cell">
              {{ formatedData(item.date) }}
            </td>
            <td class="required-docs-table__cell">
              {{ item.osLis }}
            </td>
            <td class="required-docs-table__cell">
              {{ item.patientName }}
            </td>
            <td class="required-docs-table__cell">
              {{ item.numberAlvaroOrder || 'Não Transferida' }}
            </td>
            <td class="required-docs-table__cell required-docs-table__cell--status">
              <DocumentIcon :is-document-delivered="allDocumentsDelivered(item)" />
            </td>
            <td class="required-docs-table__cell required-docs-table__cell--origem">
              {{ item.origin }}
            </td>
          </tr>
          <transition name="slide">
            <tr
              v-show="showCollapse === index"
              class="required-docs-table__row-collapse"
              @click.stop
            >
              <td colspan="7">
                <collapse-table
                  :os-alvaro="item.numberAlvaroOrder"
                  :exams="item.examOrder"
                  @updateTableItemExams="exams => updateTableItemExams(index, exams)"
                />
              </td>
            </tr>
          </transition>
        </tbody>
      </template>
      <template v-else>
        <tbody>
          <tr>
            <td colspan="6" />
          </tr>
          <tr>
            <td
              colspan="6"
              class="no-results"
            >
              Sem resultados encontrados.
            </td>
          </tr>
          <tr>
            <td colspan="6" />
          </tr>
        </tbody>
      </template>
    </table>
  </div>
</template>

<script>
import Moment from 'moment'
import CollapseTable from './CollapseTable.vue'
import DocumentIcon from '@/components/Documents/Laboratory/Flag'
import Spinner from '@/components/common/Spinner'
import { mapGetters } from 'vuex'

export default {
  name: 'RequiredDocsTable',
  components: {
    CollapseTable,
    DocumentIcon,
    Spinner
  },
  props: {
    data: Array,
    loadingDocs: Boolean,
    filterInput: String
  },
  data() {
    return {
      showCollapse: null,
      actualColumnSortType: null,
      sortDirection: 'asc',
      sortedDocs: []
    }
  },
  computed: {
    ...mapGetters(['isNewDocModalOpen']),
    filteredData() {
      if (!this.filterInput) {
        return this.data
      }
      const filterText = this.filterInput.toLowerCase()
      return this.data.filter(item => {
        const propertiesToSearch = ['date', 'osLis', 'patientName', 'numberAlvaroOrder', 'origin']
        return propertiesToSearch.some(prop => {
          const value = item[prop]
          if (value) {
            return value.toString().toLowerCase().includes(filterText)
          }
          return false
        })
      })
    },
    docs() {
      return this.sortedDocs.length ? this.sortedDocs : this.filteredData
    }
  },
  watch: {
    filterInput() {
      this.sortedDocs = []
    }
  },
  methods: {
    toggleCollapse(index) {
      this.showCollapse = this.showCollapse === index ? null : index
    },
    formatedData(data) {
      return Moment(data).format('DD/MM/YYYY')
    },
    orderByColumn(propName, columnIndex) {
      if (this.actualColumnSortType === columnIndex) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.actualColumnSortType = columnIndex
        this.sortDirection = 'asc'
      }

      const sortedList = [...this.filteredData].sort((a, b) => {
        let valueA = a[propName] !== undefined && a[propName] !== null ? a[propName].toString().trim() : ''
        let valueB = b[propName] !== undefined && b[propName] !== null ? b[propName].toString().trim() : ''

        if (propName === 'date') {
          const dateA = new Date(valueA)
          const dateB = new Date(valueB)
          return this.sortDirection === 'asc' ? dateA - dateB : dateB - dateA
        } else if (propName === 'hasDocPendente') {
          return this.sortDirection === 'asc'
            ? a.hasDocPendente - b.hasDocPendente
            : b.hasDocPendente - a.hasDocPendente
        } else if (this.sortDirection === 'asc') {
          return valueA.localeCompare(valueB, undefined, { numeric: true })
        } else {
          return valueB.localeCompare(valueA, undefined, { numeric: true })
        }
      })

      this.sortedDocs = sortedList
    },
    allDocumentsDelivered(os) {
      return os.examOrder.every(item => item.docDelivered === true)
    },
    updateTableItemExams(index, data) {
      this.docs[index].examOrder = data
      this.docs[index].examOrder.forEach(exam => {
        exam.docDelivered = exam.documentExam.every(item => item.documentExamUpload !== null)
      })
    }
  }
}
</script>

<style lang="scss">
  .required-docs-table {
    background-color: #fff;
    width: 100%;
    margin: 0;
    z-index: 1;
    border-radius: 8px;
    font-family: 'Exo 2' !important;

    &__spinner {
      margin: 50px auto !important;
    }

    &__table {
      width: 100%;
      padding: 12px 24px;
      text-align: left;
      border-spacing: 0;
    }

    &__header-cell,
    &__cell {
      padding: 10px 2px;
      text-overflow: ellipsis;
      overflow: hidden;
      width: inherit;
      white-space: nowrap;
      font-family: 'Exo 2' !important;
    }

    &__header-cell {
      font-size: 11px;
      font-weight: 900;
      color: #717171;
      letter-spacing: 2px;
      border-bottom: 2px solid #797979;
      padding: 6px 2px;
      cursor: pointer;

      &--date {
        width: 120px;
      }

      &--os-lis {
        width: 150px;
      }

      &--os-alvaro {
        width: 150px;
      }

      &--status,
      &--origem {
        width: 100px;
        text-align: center;
      }
    }

    &__cell {
      font-size: 14px;
      color: #797979;
      background-color: #fff;
      line-height: 14px;
      padding: 12px 2px;
      transition: all 0.12s ease-in-out;

      &--status,
      &--origem {
        text-align: center;
      }
    }

    &__row-collapse {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.12s ease-in-out;
      transform-origin: top;
    }

    .table-body {
      &--0 {
        .required-docs-table__cell {
          padding-top: 18px;
        }
      }

      &:hover {
        .required-docs-table__cell {
          cursor: pointer;
        }
      }
    }

    .no-results {
      padding: 50px;
      text-align: center;
      font-size: 16px;
    }

    .slide-enter-active,
    .slide-leave-active {
      transition: transform 0.12s ease-in-out;
    }

    .slide-enter,
    .slide-leave-to {
      transform: scaleY(0);
    }

    .show-collapse {
      max-height: 1000px;
      transform: scaleY(1);
    }

    .arrow-direction {
      margin: 0;
      margin-left: 4px;
      width: 13px;
      min-width: 13px;

      svg {
        width: 13px;
      }

      &__up {
        .icon-sort-arrow-up {
          fill: #dbdbdb;
        }
      }

      &__down {
        .icon-sort-arrow-down {
          fill: #dbdbdb;
        }
      }
    }
  }
</style>
